import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Crown, ExternalLink, Share2, Heart, BookMarked } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import Seo from "@/components/Seo";

export default function PublicProfile() {
  const { id } = useParams();
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("owned");
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get(`/users/${id}/public`).then((r) => {
      if (!cancel) setProfile(r.data);
    }).catch(() => {
      if (!cancel) setNotFound(true);
    }).finally(() => {
      if (!cancel) setLoading(false);
    });
    return () => { cancel = true; };
  }, [id]);

  useEffect(() => {
    if (!profile) return;
    api.get(`/users/${id}/collection/public`, { params: { status } }).then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, [id, status, profile]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: profile?.display_name, url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success(t("profile.copied", "Lien copié"));
      }
    } catch {}
  };

  if (loading) return <div className="text-center text-white/40 font-mono py-20 tracking-widest text-xs">LOADING…</div>;
  if (notFound || !profile) {
    return (
      <div className="page-enter max-w-[600px] mx-auto px-6 py-20 text-center">
        <h1 className="font-heading font-black text-4xl tracking-tighter mb-3">{t("profile.private", "Profil privé")}</h1>
        <p className="text-white/60 mb-6">{t("profile.private_desc", "Ce collectionneur n'a pas rendu son profil public.")}</p>
        <Link to="/feed" className="btn-ghost text-xs uppercase tracking-wider">← {t("feed.title", "Fil communautaire")}</Link>
      </div>
    );
  }

  return (
    <div className="page-enter max-w-[1200px] mx-auto px-6 lg:px-10 py-12">
      <Seo
        title={`${profile.display_name}'s Pokémon TCG collection`}
        description={`${profile.bio || `Explore ${profile.display_name}'s public Pokémon card collection — ${profile.owned_count} owned, ${profile.favorite_count} favorites.`}`}
        url={`${window.location.origin}/u/${profile.id}`}
        type="profile"
      />
      <div className="mb-10">
        <div className="font-mono text-xs text-[#00E5FF] tracking-widest mb-2">// PUBLIC_PROFILE</div>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading font-black text-5xl tracking-tighter inline-flex items-center gap-3" data-testid="profile-name">
              {profile.display_name}
              {profile.is_premium && <Crown className="w-9 h-9 text-[#FACC15]" />}
            </h1>
            {profile.bio && <p className="text-white/60 mt-3 max-w-2xl">{profile.bio}</p>}
            <div className="flex gap-6 mt-4 font-mono text-xs text-white/50 tracking-wider">
              <span><BookMarked className="w-3 h-3 inline mr-1" /> {profile.owned_count} owned</span>
              <span><Heart className="w-3 h-3 inline mr-1" /> {profile.favorite_count} favorites</span>
              {profile.joined_at && <span>joined {new Date(profile.joined_at).toLocaleDateString()}</span>}
            </div>
          </div>
          <button onClick={share} className="btn-ghost text-xs uppercase tracking-wider inline-flex items-center gap-2" data-testid="profile-share-btn">
            <Share2 className="w-3.5 h-3.5" /> {t("profile.share", "Partager")}
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {["owned", "favorite", "wishlist"].map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            data-testid={`profile-tab-${s}`}
            className={`px-4 py-1.5 font-mono text-[10px] uppercase tracking-widest border transition ${status === s ? "bg-[#FACC15] text-black border-[#FACC15]" : "border-white/20 text-white/60 hover:border-white/50"}`}
          >
            {s}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="flat-card p-12 text-center text-white/50 text-sm">{t("profile.empty", "Aucune carte dans cette liste.")}</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="profile-grid">
          {items.map((it) => (
            <Link key={it.id} to={`/cards/${it.card_id}`} className="flat-card p-2 hover:border-[#FACC15]/40 transition group">
              {it.snapshot?.image ? (
                <img src={it.snapshot.image} alt={it.snapshot.name} className="w-full aspect-[3/4] object-cover" />
              ) : (
                <div className="w-full aspect-[3/4] bg-black/30" />
              )}
              <div className="p-2">
                <div className="font-heading font-bold text-xs truncate">{it.snapshot?.name}</div>
                <div className="font-mono text-[9px] text-white/40 tracking-wider truncate">{it.snapshot?.set_name} · #{it.snapshot?.number}</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
